import React from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';

import TouchableElement from '@components/common/touchableElement';
import MachineNameAndStatus from '@v2/pages/machines/common/components/MachineNameAndStatus/MachineNameAndStatus';
import Svg from '@styles/icons/svg';
import ColorWrapper from '@styles/colors';

import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { ColorFunction } from '@styles/colors/types';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { OPERATOR_PANEL_INFORMATION_SCREENS, scheduledProductionOperationIdParam } from '@components/operatorPanel/constants/operatorPanel.constants';
import i18n from 'i18n-js';
import Buttons from '@styles/buttons';
import { useManageOPInformationTabs } from './hooks/useManageOPInformationTabs';
import { SmallLoading } from '@components/loading';
import { MachineStatusBulletDisplayVariant } from '@v2/pages/machines/common/components/MachineNameAndStatus/types';
import { TAB_TEST_IDS } from './constants';
import { isOPTabVisible } from '@components/operatorPanel/helpers/isOPTabVisible';

export interface OperatorPanelInformationTabsProps {
  C: ColorFunction;
  selectedTab: string;
  font?: typeof initialFont;
  scale?: ScaleFunction;
}

const OperatorPanelInformationTabs: React.FC<OperatorPanelInformationTabsProps> = ({ C, selectedTab, font = initialFont, scale = initialScale }) => {
  const navigation = useNavigation<any>();

  const theme = C.scheme === 'light' ? 'Light' : 'Dark';
  const borderColor = C(`Hairline/${theme}/Transpar [mobile]`, false);

  const {
    isMachinePerspective,
    selectedJob,
    selectedJobId,
    machineId,
    shouldDisplayUpdateBatchIdButton,
    onUpdateBatchPress,
    tabs,
    selectedItem,
    tabsLoading,
    requirementsContext,
  } = useManageOPInformationTabs();

  // TEMP: for testing purpose
  // const isWithERP = false;

  return (
    <View style={{ width: '100%', flex: 1 }}>
      <View style={{ paddingHorizontal: 25, marginTop: scale(29), marginBottom: scale(21), flexShrink: 0 }}>
        {selectedItem && isMachinePerspective ? <MachineNameAndStatus scale={scale} font={font} selectedItem={selectedItem} statusDisplayVariant={MachineStatusBulletDisplayVariant.VISIBLE} /> : null}
        {!isMachinePerspective && (
          <Text style={[font['Headline Large'], { color: C('Label/Light/Primary') }]}>
            {i18n.t('Job')}
            {selectedJob ? ` ${selectedJob?.productionOperationNumber}` : ''}
          </Text>
        )}
      </View>
      <View style={{ paddingHorizontal: 16, flex: 1 }}>
        <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: scale(24) }}>
          {tabs.map((item, index) => {
            if (!isOPTabVisible(item, requirementsContext)) {
              return null;
            }

            // Render separator
            if (item?.isSeparator) {
              return (
                <View key={`separator-${index}`} style={{ marginTop: scale(16), marginBottom: scale(8) }}>
                  <View style={{ height: StyleSheet.hairlineWidth, backgroundColor: C('Hairline/Light/Transpar [mobile]') }} />
                  <Text style={[font['Headline'], { color: C('Label/Light/Tertiary'), marginTop: scale(8) }]}>
                    {i18n.t(item.title)}
                  </Text>
                </View>
              );
            }

            const selected = item.value === selectedTab;
            let title = item.title;
            if (item.title === 'Jobs' && !isMachinePerspective) {
              title = 'Job';
            }
            if (item.title === 'NC File' && !isMachinePerspective) {
              title = 'NC Files';
            }

            return (
              <TouchableElement
                key={`${selected}${item.value}`}
                testID={TAB_TEST_IDS[item.value as OPERATOR_PANEL_INFORMATION_SCREENS]}
                onPress={() => {
                  // Prepare clean params for navigation to avoid nested structure
                  const navigationParams: any = {};
                  if (selectedJobId) {
                    navigationParams[scheduledProductionOperationIdParam] = selectedJobId;
                  }
                  if (machineId) {
                    navigationParams.id = machineId;
                  }
                  navigation.navigate(OPERATOR_PANEL_INFORMATION_SCREENS.Information, { screen: item.value, params: navigationParams });
                }}
                hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}
              >
                {({ pressHoverColor, boxStyle, hovered, pressed }) => (
                  <View
                    style={[
                      {
                        paddingHorizontal: scale(7),
                        alignItems: 'center',
                        flexDirection: 'row',
                        height: scale(44),
                        borderRadius: scale(10),
                        backgroundColor: C(
                          pressHoverColor(
                            selected ? 'Selected/Light/Left Pane Menu Item' : hovered || pressed ? 'Button/Light/Only Text' : 'transparent'
                          )
                        ),
                      },
                      boxStyle,
                    ]}
                  >
                    <Svg
                      icon={selected && item?.iconSelected ? item.iconSelected : item.icon}
                      color={selected ? 'Label/Light/AlwaysLight Primary' : 'Label/Light/Primary'}
                      width={scale(32)}
                      height={scale(24)}
                    />
                    <Text
                      style={[font['Body'], { color: C(selected ? 'Label/Light/AlwaysLight Primary' : 'Label/Light/Primary'), marginLeft: scale(7) }]}
                    >
                      {item?.translationTitlePart ? title.replace(item.translationTitlePart, i18n.t(item.translationTitlePart)) : i18n.t(title)}
                    </Text>
                    <View
                      style={{
                        backgroundColor: selected || C.scheme === 'dark' ? 'transparent' : C('Button/Light/Secondary'),
                        marginLeft: 'auto',
                        paddingHorizontal: scale(7),
                        borderRadius: scale(11),
                        minHeight: scale(24),
                        minWidth: scale(24),
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {tabsLoading ? (
                        <SmallLoading />
                      ) : (
                        <Text style={[{ color: C(selected ? 'Label/Light/AlwaysLight Primary' : 'Label/Light/Tertiary') }, font['Body']]}>
                          {item.count}
                        </Text>
                      )}
                    </View>
                  </View>
                )}
              </TouchableElement>
            );
          })}
        </ScrollView>
      </View>
      {shouldDisplayUpdateBatchIdButton && (
        <View
          style={{
            paddingHorizontal: 16,
            borderTopColor: borderColor,
            borderTopWidth: StyleSheet.hairlineWidth,
            paddingTop: scale(9),
            paddingBottom: scale(18),
            gap: scale(9),
            height: scale(115),
          }}
        >
          <Text style={[font.Footnote, { color: C('Label/Light/Secondary'), textAlign: 'center', gap: scale(9), alignItems: 'center' }]}>
            {i18n.t('The machine has started a new NC File. Do you want to update this screen?')}
          </Text>
          <View style={{ alignSelf: 'center', flexShrink: 1 }}>
            <Buttons {...{ variant: 'M', type: 'Secondary', onPress: onUpdateBatchPress, scale, font }}>{i18n.t('Update')}</Buttons>
          </View>
        </View>
      )}
    </View>
  );
};

export default ColorWrapper(OperatorPanelInformationTabs);
